import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Stack,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { QRCodeSVG } from "qrcode.react";

import LabelIcon from "@mui/icons-material/Label";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import LabelPreview from "./LabelPreview";
import DymoUpdateDialog from "./DymoUpdateDialog";

// Configuration object
const CONFIG = {
  LABEL_TEXT_LINE1: "111111111", // SERNUM from API
  LABEL_TEXT_LINE2: "A2134", // PARTNAME from API
  DEFAULT_DEVICE_NUMBER: "",
  QR_BASE_URL: "https://neopharmportal.azurewebsites.net/device/",
};

// Hebrew translations
const HEBREW_TEXT = {
  APP_TITLE: "🏷️ מדפסת תוויות DYMO",
  APP_SUBTITLE: "Eldan.label-7 - תוויות QR ותיבת טקסט מותאמות אישית",
  LOADING: "טוען מסגרת DYMO...",
  INSTRUCTIONS_TITLE: "📋 הוראות",
  INSTRUCTIONS: {
    DEVICE_NUMBER: "הזן מספר מכשיר ליצירת כתובת QR קוד",
    LABEL_CODES: "התווית תציג קודי מוצר ברירת מחדל",
    DYMO_RUNNING: "ודא שתוכנת DYMO Connect פועלת",
  },
  DEVICE_NUMBER_LABEL: "📱 מספר מכשיר",
  DEVICE_NUMBER_PLACEHOLDER: "הזן מספר מכשיר (לדוגמה: 2928A)",
  QR_CODE_URL: "כתובת QR קוד:",
  SELECT_PRINTER: "🖨️ בחר מדפסת",
  LOADING_PRINTERS: "טוען מדפסות...",
  CONNECTED: "מחובר ✅",
  OFFLINE: "לא מקוון ❌",
  PRINT_LABEL: "🖨️ הדפס תווית",
  MODEL: "דגם:",
  STATUS: "סטטוס:",
  TYPE: "סוג:",

  // Status messages
  STATUS_MESSAGES: {
    LABEL_SENT: "תווית נשלחה למדפסת בהצלחה!",
    SELECT_PRINTER: "אנא בחר מדפסת",
  },

  // Error messages
  ERROR_MESSAGES: {
    NO_PRINTERS: `לא נמצאו מדפסות DYMO. אנא בדוק את הפריטים הבאים:

1. ודא שתוכנת DYMO Label (DYMO Connect) פתוחה ופועלת
2. וודא שמדפסת DYMO מחוברת למחשב הנייד/מחשב באמצעות USB
3. בדוק שמדפסת DYMO מחוברת לחשמל ומופעלת
4. נסה לנתק ולחבר מחדש את כבל USB
5. הפעל מחדש את תוכנת DYMO Connect במידת הצורך

לאחר בדיקת פריטים אלה, לחץ על "טען תווית Eldan.label-7" כדי לרענן את רשימת המדפסות.`,
    PRINT_FAILED: "הדפסה נכשלה:",
    PREVIEW_ERROR: "שגיאה ביצירת תצוגה מקדימה:",
    CONTENT_UPDATE_WARNING: "אזהרה: לא ניתן לעדכן תוכן תווית",
    FALLBACK_LABEL_FAILED: "נכשל ביצירת תווית גיבוי:",
    PREVIEW_GENERATION_FAILED: "נכשל ביצירת תמונת תצוגה מקדימה",
  },
};

const DymoLabelPrinter = ({ user }) => {
  const { accounts } = useMsal();
  
  // State management
  const [deviceNumber, setDeviceNumber] = useState("");
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [currentLabel, setCurrentLabel] = useState(null);
  const [labelPreview, setLabelPreview] = useState("");
  const [status, setStatus] = useState({ message: "", type: "" });
  const [isFrameworkReady, setIsFrameworkReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [isLoadingDeviceData, setIsLoadingDeviceData] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateSaving, setUpdateSaving] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null); // { type: 'success'|'error', message }

  // Add a ref to store the timeout ID
  const deviceNumberTimeoutRef = useRef(null);

  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanMobileUrl, setScanMobileUrl] = useState("");
  const scanSseRef = useRef(null);

  // Mock functions for demo purposes
  const showStatus = useCallback((message, type) => {
    setStatus({ message, type });

    if (type === "success") {
      setTimeout(() => {
        setStatus({ message: "", type: "" });
      }, 5000);
    }
  }, []);

  // Helper function to get label data (eliminates duplication across preview functions)
  const getLabelData = useCallback(() => {
    const line1Text = deviceData?.value?.[0]?.PARTNAME || CONFIG.LABEL_TEXT_LINE2;
    const line2Text = deviceData?.value?.[0]?.SERNUM || CONFIG.LABEL_TEXT_LINE1;
    const finalQrUrl = qrCodeUrl || `https://neopharmportal.azurewebsites.net/device/${deviceNumber}`;
    const urlPath = qrCodeUrl ? qrCodeUrl.split("/device/")[1] : deviceNumber;
    
    return { line1Text, line2Text, finalQrUrl, urlPath };
  }, [deviceData, qrCodeUrl, deviceNumber]);

  // Function to fetch device data from backend
  const fetchDeviceData = useCallback(
    async (deviceNum) => {
      if (!deviceNum || deviceNum.trim() === "") {
        setDeviceData(null);
        setQrCodeUrl("");
        return;
      }

      setIsLoadingDeviceData(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/dymo-label/device/${deviceNum}`
        );
        const result = await response.json();

        if (result.success) {
          setDeviceData(result.data);
          setQrCodeUrl(
            result.qrCodeUrl ||
              `https://neopharmportal.azurewebsites.net/device/${deviceNum}`
          );
          showStatus(`נתוני מכשיר ${deviceNum} נטענו בהצלחה`, "success");
        } else {
          setDeviceData(null);
          setQrCodeUrl("");
          showStatus(result.message || "שגיאה בטעינת נתוני המכשיר", "error");
        }
      } catch (error) {
        console.error("Error fetching device data:", error);
        setDeviceData(null);
        setQrCodeUrl("");
        showStatus("שגיאה בחיבור לשרת", "error");
      } finally {
        setIsLoadingDeviceData(false);
      }
    },
    [showStatus]
  );

  // Mock functions
  const loadSpecificLabel = useCallback(() => {
    setCurrentLabel({ loaded: true });
    setButtonsEnabled(true);
    showStatus(HEBREW_TEXT.STATUS_MESSAGES.LABEL_LOADED, "success");

    // Automatically generate preview when label is loaded
    setTimeout(() => {
      // Create a simple base64 encoded placeholder image
      const canvas = document.createElement("canvas");
      canvas.width = 300;
      canvas.height = 200;
      const ctx = canvas.getContext("2d");

      // Create a mock label design
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 300, 200);

      // Border
      ctx.strokeStyle = "#cccccc";
      ctx.lineWidth = 2;
      ctx.strokeRect(5, 5, 290, 190);

      // Title text
      ctx.fillStyle = "#333333";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "center";
      ctx.fillText("DYMO Label Preview", 150, 30);

      // QR Code placeholder
      ctx.fillStyle = "#000000";
      ctx.fillRect(20, 50, 80, 80);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(25, 55, 70, 70);

      // Add QR pattern
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 8; j++) {
          if ((i + j) % 2 === 0) {
            ctx.fillStyle = "#000000";
            ctx.fillRect(30 + i * 8, 60 + j * 8, 6, 6);
          }
        }
      }

      // Product codes - use actual device data if available
      const { line1Text, line2Text, urlPath } = getLabelData();
      
      ctx.fillStyle = "#333333";
      ctx.font = "14px Arial";
      ctx.textAlign = "left";
      ctx.fillText(line1Text, 120, 70);
      ctx.fillText(line2Text, 120, 90);

      // Device number
      ctx.font = "12px Arial";
      ctx.fillText(`Device: ${deviceNumber}`, 120, 110);

      // URL
      ctx.font = "10px Arial";
      ctx.fillText("neopharmportal.azurewebsites.net", 20, 150);
      ctx.fillText(`/device/${urlPath}`, 20, 165);

      // Convert to base64 and set as preview
      const dataURL = canvas.toDataURL("image/png");
      setLabelPreview(dataURL);
      showStatus(HEBREW_TEXT.STATUS_MESSAGES.LABEL_PREVIEW_UPDATED, "success");
    }, 500);
  }, [showStatus, getLabelData, deviceNumber]);

  // Simulate framework initialization
  useEffect(() => {
    setIsLoading(true);

    // Initialize DYMO framework like the HTML version
    const initializeDymo = async () => {
      try {
        // Check if DYMO framework is available
        if (
          typeof window.dymo === "undefined" ||
          !window.dymo.label ||
          !window.dymo.label.framework
        ) {
          setIsFrameworkReady(false);
          setIsLoading(false);
          setAvailablePrinters([]);
          showStatus("DYMO Connect framework לא זמין - אנא ודא שהתוכנה פועלת", "error");
          return;
        }

        // Initialize DYMO framework with better error handling
        const initDymoFramework = () => {
          try {
            window.dymo.label.framework.init(() => {
              try {
                // Try different methods to get printers based on DYMO framework version
                let printers = [];

                // Method 1: Try getPrinters() synchronous method
                if (window.dymo.label.framework.getPrinters) {
                  printers = window.dymo.label.framework.getPrinters();
                }
                // Method 2: Try getLabelWriterPrinters() if available
                else if (window.dymo.label.framework.getLabelWriterPrinters) {
                  printers = window.dymo.label.framework.getLabelWriterPrinters();
                }
                // Method 3: Check if it's async but without Promise support
                else if (window.dymo.label.framework.getPrintersAsync) {
                  // Some versions return a callback-based async function
                  window.dymo.label.framework.getPrintersAsync((result) => {
                    printers = result || [];
                    processPrinters(printers);
                  });
                  return; // Exit early for callback version
                }

                // Process printers for synchronous methods
                processPrinters(printers);
              } catch (error) {
                console.error("Error getting printers:", error);
                showStatus("Failed to load printers: " + error.message, "error");
                setIsLoading(false);
              }

          function processPrinters(printers) {
            if (printers && printers.length > 0) {
              // Handle different printer object structures
              const formattedPrinters = printers.map((p) => {
                // Some versions return simple strings, others return objects
                if (typeof p === "string") {
                  return {
                    name: p,
                    isConnected: true, // If printer is found, consider it available
                    modelName: p,
                    printerType: "LabelWriter",
                    showChip: p.includes("LabelWriter 550"), // Only show chip for LabelWriter 550
                  };
                } else {
                  const printerName = p.name || p.printerName || p;
                  return {
                    name: printerName,
                    isConnected: true, // If printer is found, consider it available
                    modelName: p.modelName || p.model || p.name,
                    printerType: p.printerType || p.type || "LabelWriter",
                    showChip: printerName.includes("LabelWriter 550"), // Only show chip for LabelWriter 550
                  };
                }
              });

              setAvailablePrinters(formattedPrinters);

              // Auto-select first printer
              if (formattedPrinters.length > 0) {
                setSelectedPrinter(formattedPrinters[0].name);
              }

              showStatus(
                `נמצאו ${formattedPrinters.length} מדפסות DYMO זמינות`,
                "success"
              );
            } else {
              showStatus(HEBREW_TEXT.ERROR_MESSAGES.NO_PRINTERS, "error");
            }

            setIsFrameworkReady(true);
            setIsLoading(false);

            // Automatically load label after printers are loaded
            setTimeout(() => {
              setCurrentLabel({ loaded: true });
              setButtonsEnabled(true);

              // Generate initial preview
              setTimeout(() => {
                const canvas = document.createElement("canvas");
                canvas.width = 300;
                canvas.height = 200;
                const ctx = canvas.getContext("2d");

                // Create a mock label design
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, 300, 200);

                // Border
                ctx.strokeStyle = "#cccccc";
                ctx.lineWidth = 2;
                ctx.strokeRect(5, 5, 290, 190);

                // Title text
                ctx.fillStyle = "#333333";
                ctx.font = "bold 16px Arial";
                ctx.textAlign = "center";
                ctx.fillText("DYMO Label Preview", 150, 30);

                // QR Code placeholder
                ctx.fillStyle = "#000000";
                ctx.fillRect(20, 50, 80, 80);
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(25, 55, 70, 70);

                // Add QR pattern
                for (let i = 0; i < 8; i++) {
                  for (let j = 0; j < 8; j++) {
                    if ((i + j) % 2 === 0) {
                      ctx.fillStyle = "#000000";
                      ctx.fillRect(30 + i * 8, 60 + j * 8, 6, 6);
                    }
                  }
                }

                // Product codes - use default values initially
                ctx.fillStyle = "#333333";
                ctx.font = "14px Arial";
                ctx.textAlign = "left";
                ctx.fillText(CONFIG.LABEL_TEXT_LINE1, 120, 70);
                ctx.fillText(CONFIG.LABEL_TEXT_LINE2, 120, 90);

                // Device number (initially empty)
                ctx.font = "12px Arial";
                ctx.fillText("Device: (enter device number)", 120, 110);

                // URL
                ctx.font = "10px Arial";
                ctx.fillText("neopharmportal.azurewebsites.net", 20, 150);
                ctx.fillText("/device/[number]", 20, 165);

                // Convert to base64 and set as preview
                const dataURL = canvas.toDataURL("image/png");
                setLabelPreview(dataURL);
                showStatus(
                  HEBREW_TEXT.STATUS_MESSAGES.LABEL_PREVIEW_UPDATED,
                  "success"
                );
              }, 500);
            }, 1000);
          }
        });
      } catch (error) {
        console.error("Error initializing DYMO framework:", error);
        
        // If framework is busy, try again after a delay
        if (error.message.includes("service discovery is in progress")) {
          setTimeout(() => {
            initDymoFramework();
          }, 2000);
          return;
        }
        
        setIsLoading(false);
        showStatus(`שגיאה באתחול DYMO: ${error.message}`, "error");
      }
    };

        // Start initialization
        initDymoFramework();
      } catch (error) {
        console.error("Error initializing DYMO:", error);
        setIsLoading(false);
        showStatus(`שגיאה באתחול DYMO: ${error.message}`, "error");
      }
    };

    initializeDymo();
  }, [showStatus]); // Only depend on showStatus

  // Update preview when device number changes (separate effect)
  useEffect(() => {
    if (currentLabel && deviceNumber) {
      // Update preview with new device number
      setTimeout(() => {
        const canvas = document.createElement("canvas");
        canvas.width = 300;
        canvas.height = 200;
        const ctx = canvas.getContext("2d");

        // Create a mock label design
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, 300, 200);

        // Border
        ctx.strokeStyle = "#cccccc";
        ctx.lineWidth = 2;
        ctx.strokeRect(5, 5, 290, 190);

        // Title text
        ctx.fillStyle = "#333333";
        ctx.font = "bold 16px Arial";
        ctx.textAlign = "center";
        ctx.fillText("DYMO Label Preview", 150, 30);

        // QR Code placeholder
        ctx.fillStyle = "#000000";
        ctx.fillRect(20, 50, 80, 80);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(25, 55, 70, 70);

        // Add QR pattern
        for (let i = 0; i < 8; i++) {
          for (let j = 0; j < 8; j++) {
            if ((i + j) % 2 === 0) {
              ctx.fillStyle = "#000000";
              ctx.fillRect(30 + i * 8, 60 + j * 8, 6, 6);
            }
          }
        }

        // Product codes - use actual device data if available
        const { line1Text, line2Text, urlPath } = getLabelData();
        
        ctx.fillStyle = "#333333";
        ctx.font = "14px Arial";
        ctx.textAlign = "left";
        ctx.fillText(line1Text, 120, 70);
        ctx.fillText(line2Text, 120, 90);

        // Device number
        ctx.font = "12px Arial";
        ctx.fillText(`Device: ${deviceNumber}`, 120, 110);

        // URL
        ctx.font = "10px Arial";
        ctx.fillText("neopharmportal.azurewebsites.net", 20, 150);
        ctx.fillText(`/device/${urlPath}`, 20, 165);

        // Convert to base64 and set as preview
        const dataURL = canvas.toDataURL("image/png");
        setLabelPreview(dataURL);
      }, 100); // Short delay to avoid too many updates
    }
  }, [deviceNumber, currentLabel, getLabelData]); // getLabelData now encapsulates qrCodeUrl and deviceData

  const previewLabel = useCallback(() => {
    if (!currentLabel) {
      showStatus(HEBREW_TEXT.STATUS_MESSAGES.NO_LABEL_LOADED, "error");
      return;
    }

    // Create a simple base64 encoded placeholder image
    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 200;
    const ctx = canvas.getContext("2d");

    // Create a mock label design
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 300, 200);

    // Border
    ctx.strokeStyle = "#cccccc";
    ctx.lineWidth = 2;
    ctx.strokeRect(5, 5, 290, 190);

    // Title text
    ctx.fillStyle = "#333333";
    ctx.font = "bold 16px Arial";
    ctx.textAlign = "center";
    ctx.fillText("DYMO Label Preview", 150, 30);

    // QR Code placeholder
    ctx.fillStyle = "#000000";
    ctx.fillRect(20, 50, 80, 80);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(25, 55, 70, 70);

    // Add QR pattern
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        if ((i + j) % 2 === 0) {
          ctx.fillStyle = "#000000";
          ctx.fillRect(30 + i * 8, 60 + j * 8, 6, 6);
        }
      }
    }

    // Product codes - use actual device data if available
    const { line1Text, line2Text, urlPath } = getLabelData();
    
    ctx.fillStyle = "#333333";
    ctx.font = "14px Arial";
    ctx.textAlign = "left";
    ctx.fillText(line1Text, 120, 70);
    ctx.fillText(line2Text, 120, 90);

    // Device number
    ctx.font = "12px Arial";
    ctx.fillText(`Device: ${deviceNumber}`, 120, 110);

    // URL
    ctx.font = "10px Arial";
    ctx.fillText("neopharmportal.azurewebsites.net", 20, 150);
    ctx.fillText(`/device/${urlPath}`, 20, 165);

    // Convert to base64 and set as preview
    const dataURL = canvas.toDataURL("image/png");
    setLabelPreview(dataURL);
    showStatus(HEBREW_TEXT.STATUS_MESSAGES.LABEL_PREVIEW_UPDATED, "success");
  }, [currentLabel, showStatus, getLabelData, deviceNumber]);

  // Helper function to log print actions (success or failure)
  const logPrintAction = useCallback(async (line1Text, line2Text, finalQrUrl, status, errorDetails = null) => {
    try {
      const account = accounts[0];
      const userName = account?.name || account?.idTokenClaims?.name || user?.fullName || 'Unknown User';
      const userEmail = account?.username || user?.email || 'N/A';

      await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/dymo-label/log-print`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sernum: line2Text,
            partname: line1Text,
            qrCodeUrl: finalQrUrl,
            pairzonId: deviceData?.pairzonId || null,
            printStatus: status,
            ...(errorDetails && { errorDetails }),
            userName: userName,
            userEmail: userEmail,
            eldn_custsernum: deviceData?.value?.[0]?.ELDN_CUSTSERNUM || null,
          }),
        }
      );
    } catch (logError) {
      console.error('Failed to log print action:', logError);
      // Don't show error to user - logging failure shouldn't affect UX
    }
  }, [accounts, user, deviceData]);

  const printLabel = useCallback(async () => {
    if (!selectedPrinter) {
      showStatus(HEBREW_TEXT.STATUS_MESSAGES.SELECT_PRINTER, "error");
      return;
    }
    if (!currentLabel) {
      showStatus(HEBREW_TEXT.STATUS_MESSAGES.NO_LABEL, "error");
      return;
    }

    // Get the actual values to print - SWAPPED ORDER
    // Moved before try block so accessible in both try and catch blocks
    const { line1Text, line2Text, finalQrUrl } = getLabelData();

    try {

      // Check if DYMO framework is available
      if (typeof window.dymo === "undefined") {
        showStatus(
          "DYMO Connect framework not loaded. Please ensure DYMO Connect is running.",
          "error"
        );
        return;
      }

      // Load the label template and modify QR code URL in XML before loading
      let labelXml = await fetch("/utils/Eldan.label-7.dymo").then((r) =>
        r.text()
      );
      
      // Replace the QR code URL directly in the XML content
      const originalQrUrl = "https://neopharmgroup.com/";
      const qrUrlPattern = new RegExp(`<DataString>URL:${originalQrUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}</DataString>`, 'g');
      labelXml = labelXml.replace(qrUrlPattern, `<DataString>URL:${finalQrUrl}</DataString>`);
      
      // Also update the WebAddressDataHolder if it exists
      const webAddressPattern = new RegExp(`<DataString>${originalQrUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}</DataString>`, 'g');
      labelXml = labelXml.replace(webAddressPattern, `<DataString>${finalQrUrl}</DataString>`);
      
      const label = window.dymo.label.framework.openLabelXml(labelXml);

      // Validate the label
      if (label && label.isValidLabel && !label.isValidLabel()) {
        showStatus("Invalid label format", "error");
        return;
      }

      // Get label objects using correct DYMO Connect Framework method
      const labelObjects = label.GetLabelObjects ? label.GetLabelObjects() : null;
      
      if (labelObjects) {
        // Use the new DYMO Connect Framework approach
        for (const labelObject of labelObjects) {
          const objectName = labelObject.ObjectName || labelObject.Name;
          
          if (objectName === "TextObject1") {
            // TextObject1 gets the second line (SERNUM)
            label.UpdateLabelObject(labelObject, line2Text);
          } else if (objectName === "TextObject12") {
            // TextObject12 gets the first line (PARTNAME) 
            label.UpdateLabelObject(labelObject, line1Text);
          } else if (objectName === "QRCodeObject0") {
            // QR code URL has already been updated in the XML before loading
            // No need to update the object here since XML was pre-modified
          }
        }
      } else {
        // Fallback to older framework methods if GetLabelObjects is not available
        if (label && label.getObjectCount && label.getObjectCount() > 0) {
          for (let i = 0; i < label.getObjectCount(); i++) {
            const objectName = label.getObjectName(i);
            const objectType = label.getObjectType(objectName);

            if (objectType === "TextObject") {
              if (objectName === "TextObject1") {
                label.setObjectText(objectName, line2Text);
              } else if (objectName === "TextObject12") {
                label.setObjectText(objectName, line1Text);
              }
            } else if (objectType === "BarcodeObject" || objectType === "QRCodeObject") {
              // QR code URL has already been updated in the XML before loading
              // No need to update the object here since XML was pre-modified
            }
          }
        } else {
          // Last resort fallback
          if (label.setObjectText) {
            try {
              label.setObjectText("TextObject1", line2Text);
              label.setObjectText("TextObject12", line1Text);
              
              // QR code URL has already been updated in the XML before loading
              // No need to update the object here since XML was pre-modified
            } catch (e) {
              // Silently fail if fallback doesn't work
            }
          }
        }
      }

      // Try using the simpler older print method first
      try {
        // Method 1: Try the simple print method (older framework)
        if (label && label.print && typeof label.print === 'function') {
          label.print(selectedPrinter);
        } else {
          // Method 2: Use the framework print method with minimal XML
          const printParamsXml = '<?xml version="1.0" encoding="utf-8"?><PrintParams><PrinterName>' + 
                                selectedPrinter + '</PrinterName><Copies>1</Copies></PrintParams>';
          
          const labelSetXml = ""; // Empty for single label
          
          window.dymo.label.framework.printLabel(
            selectedPrinter, 
            printParamsXml, 
            labelXml, 
            labelSetXml
          );
        }
      } catch (printError) {
        console.error("Print method error:", printError);
        throw printError;
      }

      showStatus(HEBREW_TEXT.STATUS_MESSAGES.LABEL_SENT, "success");
      
      // Log the print action to backend
      await logPrintAction(line1Text, line2Text, finalQrUrl, 'success');
    } catch (error) {
      console.error("Printing error:", error);
      showStatus(`שגיאה בהדפסה: ${error.message}`, "error");
      
      // Log the failed print action to backend
      await logPrintAction(line1Text, line2Text, finalQrUrl, 'failed', error.message);
    }
  }, [
    selectedPrinter,
    currentLabel,
    showStatus,
    getLabelData,
    logPrintAction,
    accounts,
  ]);

  // Improved debounced device data fetching
  const debouncedFetchDeviceData = useCallback(
    (deviceNum) => {
      // Clear existing timeout
      if (deviceNumberTimeoutRef.current) {
        clearTimeout(deviceNumberTimeoutRef.current);
      }

      // Set new timeout
      deviceNumberTimeoutRef.current = setTimeout(() => {
        fetchDeviceData(deviceNum);
      }, 1500); // Wait 1.5 seconds after user stops typing
    },
    [fetchDeviceData]
  );

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (deviceNumberTimeoutRef.current) {
        clearTimeout(deviceNumberTimeoutRef.current);
      }
    };
  }, []);

  const handleUpdateConfirm = useCallback(
    async ({ eldnCustSernum, custName, dCode }) => {
      setUpdateSaving(true);
      setUpdateStatus(null);
      try {
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/dymo-label/device/${deviceNumber}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              partname: deviceData?.value?.[0]?.PARTNAME || "",
              eldnCustSernum,
              custName,
              dCode,
            }),
          },
        );
        const result = await resp.json();
        if (!resp.ok) {
          setUpdateStatus({ type: "error", message: result.error || "שגיאה בעדכון" });
          return;
        }
        setUpdateDialogOpen(false);
        setUpdateStatus({ type: "success", message: "הנתונים עודכנו בהצלחה" });
        // Refresh device data to reflect the changes
        await fetchDeviceData(deviceNumber);
        setTimeout(() => setUpdateStatus(null), 4000);
      } catch {
        setUpdateStatus({ type: "error", message: "שגיאה בחיבור לשרת" });
      } finally {
        setUpdateSaving(false);
      }
    },
    [deviceNumber, deviceData, fetchDeviceData],
  );

  const handleDeviceNumberChange = (e) => {
    const newDeviceNumber = e.target.value;
    setDeviceNumber(newDeviceNumber);

    // Clear device data immediately when input changes
    if (newDeviceNumber.trim() === "") {
      setDeviceData(null);
      setQrCodeUrl("");
      setIsLoadingDeviceData(false);
      return;
    }

    // Only start loading if device number is at least 6 characters
    if (newDeviceNumber.trim().length >= 6) {
      setIsLoadingDeviceData(true);
      debouncedFetchDeviceData(newDeviceNumber);
    } else {
      // Clear data if input is too short
      setDeviceData(null);
      setQrCodeUrl("");
      setIsLoadingDeviceData(false);
    }
  };

  const closeScanDialog = useCallback(() => {
    setScanDialogOpen(false);
    setScanMobileUrl("");
    // Keep SSE open — mobile may scan additional devices within the session window
  }, []);

  const handleOpenScanDialog = useCallback(async () => {
    const apiBase = (process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/$/, "");
    if (scanSseRef.current) {
      scanSseRef.current.close();
      scanSseRef.current = null;
    }
    try {
      const resp = await fetch(`${apiBase}/api/dymo-label/scan-session`, { method: "POST" });
      const { token } = await resp.json();

      const mobileUrl = `${window.location.origin}/mobile-scan.html?token=${token}&api=${encodeURIComponent(apiBase)}`;
      setScanMobileUrl(mobileUrl);
      setScanDialogOpen(true);

      const sse = new EventSource(`${apiBase}/api/dymo-label/scan-session/${token}/stream`);
      scanSseRef.current = sse;

      sse.onmessage = (e) => {
        const { deviceNumber: scannedNumber } = JSON.parse(e.data);
        setDeviceNumber(scannedNumber);
        debouncedFetchDeviceData(scannedNumber);
        closeScanDialog();
      };

      sse.onerror = () => sse.close();
    } catch (err) {
      console.error("Scan session error:", err);
    }
  }, [debouncedFetchDeviceData, closeScanDialog]);

  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      if (scanSseRef.current) scanSseRef.current.close();
    };
  }, []);

  const handlePrinterChange = (e) => {
    setSelectedPrinter(e.target.value);
  };

  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Paper
          elevation={24}
          sx={{
            p: 5,
            borderRadius: 4,
            background: "white",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          {/* Loading State */}
          {isLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <CircularProgress size={60} sx={{ mr: 3, color: "#667eea" }} />
              <Typography variant="h6" sx={{ color: "#666" }}>
                {HEBREW_TEXT.LOADING}
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <>
              {/* Instructions Card with Gradient */}
              <Card
                sx={{
                  mb: 2,

                  background: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  boxShadow: "none",
                }}
              >
                <CardContent sx={{ p: 2, marginLeft: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#6c757d",
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    {HEBREW_TEXT.INSTRUCTIONS_TITLE}
                  </Typography>
                  <Box component="ul" sx={{ pr: 2, m: 0, pl: 0 }}>
                    <Typography
                      component="li"
                      sx={{ mb: 0.5, fontSize: "0.8rem", color: "#6c757d" }}
                    >
                      {HEBREW_TEXT.INSTRUCTIONS.DEVICE_NUMBER}
                    </Typography>
                    <Typography
                      component="li"
                      sx={{ mb: 0.5, fontSize: "0.8rem", color: "#6c757d" }}
                    >
                      {HEBREW_TEXT.INSTRUCTIONS.LABEL_CODES}
                    </Typography>
                    <Typography
                      component="li"
                      sx={{ fontSize: "0.8rem", color: "#6c757d" }}
                    >
                      {HEBREW_TEXT.INSTRUCTIONS.DYMO_RUNNING}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={4}>
                {/* Left Column - Preview */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      bgcolor: "#f8f9fa",
                      borderRadius: 3,
                      p: 3,
                      textAlign: "center",
                      minHeight: 400,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #dee2e6",
                      position: "relative",
                    }}
                  >
                    {currentLabel ? (
                      <LabelPreview
                        deviceNumber={deviceNumber}
                        config={{
                          ...CONFIG,
                          LABEL_TEXT_LINE1:
                            deviceData?.value?.[0]?.SERNUM ||
                            CONFIG.LABEL_TEXT_LINE1,
                          LABEL_TEXT_LINE2:
                            deviceData?.value?.[0]?.PARTNAME ||
                            CONFIG.LABEL_TEXT_LINE2,
                          QR_BASE_URL:
                            qrCodeUrl ||
                            `https://neopharmportal.azurewebsites.net/device/${deviceNumber}`,
                        }}
                      />
                    ) : (
                      <Box textAlign="center">
                        <LabelIcon
                          sx={{ fontSize: 80, color: "#dee2e6", mb: 2 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#666",
                          }}
                        >
                          תצוגה מקדימה של תווית תופיע כאן
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#999",
                            mt: 1,
                          }}
                        >
                          התווית נטענת אוטומטית...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                {/* Right Column - Form */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={3}>
                    {/* Device Number Input */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#555",
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {HEBREW_TEXT.DEVICE_NUMBER_LABEL}
                      </Typography>
                      <TextField
                        fullWidth
                        value={deviceNumber}
                        onChange={handleDeviceNumberChange}
                        placeholder={HEBREW_TEXT.DEVICE_NUMBER_PLACEHOLDER}
                        InputProps={{
                          sx: {
                            fontSize: "16px",
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#667eea",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#667eea",
                                boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
                              },
                            },
                          },
                          endAdornment: (
                            <>
                              {isLoadingDeviceData && (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                              )}
                              <IconButton
                                size="small"
                                onClick={handleOpenScanDialog}
                                title="סרוק עם טלפון"
                                sx={{ color: "#667eea" }}
                              >
                                <CameraAltIcon />
                              </IconButton>
                            </>
                          ),
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        {HEBREW_TEXT.QR_CODE_URL}{" "}
                        {qrCodeUrl ||
                          `https://neopharmportal.azurewebsites.net/device/${deviceNumber}`}
                      </Typography>

                      {/* Device Data Display */}
                      {deviceData &&
                        deviceData.value &&
                        deviceData.value.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "#f0f8ff",
                              borderRadius: 2,
                              border: "1px solid #e3f2fd",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#1976d2" }}
                              >
                                📋 נתוני המכשיר:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon sx={{ fontSize: "0.9rem" }} />}
                                onClick={() => setUpdateDialogOpen(true)}
                                sx={{
                                  fontSize: "0.75rem",
                                  py: 0.4,
                                  px: 1.2,
                                  borderRadius: 2,
                                  borderColor: "#1976d2",
                                  color: "#1976d2",
                                  "&:hover": { borderColor: "#1565c0", bgcolor: "#e3f2fd" },
                                }}
                              >
                                עדכון
                              </Button>
                            </Box>
                            {deviceData.value.map((item, index) => (
                              <Box key={index} sx={{ mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>מק"ט:</strong>{" "}
                                  {item['מק"ט'] || item.PARTNAME || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>תיאור מוצר:</strong>
                                  {item["תיאור מוצר"] || item.PARTDES || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>מספר מכשיר:</strong>{" "}
                                  {item["מספר מכשיר"] || item.SERNUM || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>מספר זיהוי לקוח:</strong>{" "}
                                  {item["מספר זיהוי לקוח"] || item.ELDN_CUSTSERNUM || "N/A"}
                                </Typography>
                              </Box>
                            ))}
                            {updateStatus && (
                              <Alert
                                severity={updateStatus.type}
                                sx={{ mt: 1, py: 0.5, fontSize: "0.8rem" }}
                              >
                                {updateStatus.message}
                              </Alert>
                            )}
                          </Box>
                        )}

                      {deviceData &&
                        (!deviceData.value ||
                          deviceData.value.length === 0) && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "#fff3e0",
                              borderRadius: 2,
                              border: "1px solid #ffcc02",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#f57c00",
                                textAlign: "center",
                              }}
                            >
                              ⚠️ לא נמצאו נתונים למספר מכשיר זה
                            </Typography>
                          </Box>
                        )}
                    </Box>

                    {/* Printer Selection */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#555",
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {HEBREW_TEXT.SELECT_PRINTER}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={selectedPrinter}
                          onChange={handlePrinterChange}
                          displayEmpty
                          sx={{
                            fontSize: "16px",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                              boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
                            },
                          }}
                        >
                          {availablePrinters.length === 0 ? (
                            <MenuItem value="">
                              {HEBREW_TEXT.LOADING_PRINTERS}
                            </MenuItem>
                          ) : (
                            availablePrinters.map((printer, index) => (
                              <MenuItem key={index} value={printer.name}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  width="100%"
                                >
                                  <span>{printer.name}</span>
                                  {printer.showChip && (
                                    <Chip
                                      size="small"
                                      label={
                                        printer.isConnected
                                          ? HEBREW_TEXT.CONNECTED
                                          : HEBREW_TEXT.OFFLINE
                                      }
                                      color={
                                        printer.isConnected
                                          ? "success"
                                          : "default"
                                      }
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Action Buttons */}
                    <Stack spacing={2} sx={{ mt: 4 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={printLabel}
                        disabled={!buttonsEnabled}
                        fullWidth
                        sx={{
                          py: 1.5,
                          fontSize: "16px",
                          fontWeight: 600,
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: 2,
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 5px 15px rgba(102, 126, 234, 0.4)",
                            background:
                              "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                          },
                          transition: "all 0.2s ease",
                          "&:disabled": {
                            background: "#ccc",
                            transform: "none",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {HEBREW_TEXT.PRINT_LABEL}
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}

          {/* Status Message */}
          {status.message && (
            <Box mt={3}>
              <Alert
                severity={status.type === "success" ? "success" : "error"}
                sx={{
                  textAlign: "left",
                  fontSize: "1rem",
                  fontWeight: 500,
                  borderRadius: 2,
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                {status.message}
              </Alert>
            </Box>
          )}
        </Paper>
      </Container>

      <Dialog open={scanDialogOpen} onClose={closeScanDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
          סרוק עם הטלפון לצילום מספר מכשיר
          <IconButton
            onClick={closeScanDialog}
            sx={{ position: "absolute", left: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={3}
            py={2}
          >
            {scanMobileUrl && (
              <QRCodeSVG value={scanMobileUrl} size={200} />
            )}
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                ממתין לסריקה...
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <DymoUpdateDialog
        open={updateDialogOpen}
        onClose={() => {
          if (!updateSaving) {
            setUpdateDialogOpen(false);
            setUpdateStatus(null);
          }
        }}
        onConfirm={handleUpdateConfirm}
        initialEldnCustSernum={deviceData?.value?.[0]?.ELDN_CUSTSERNUM || ""}
        saving={updateSaving}
      />
    </Box>
  );
};

export default DymoLabelPrinter;
